import { AppIntegrationsRepository } from 'common/repositories/index';
import Util from '../services/util/-RFC.util.js';

let _licenses = [];
const observableLicenses = new Rx.BehaviorSubject(_licenses);


/**
     * @ngdoc service
     * @name repositories.LicensesRepository
     *
     * @description
     * This class is used to save/add license objects.
     */
const LicensesRepository = {

    /**
         * @ngdoc function
         * @name repositories.LicensesRepository#replace
         * @methodOf repositories.LicensesRepository
         * @function
         *
         * @description
         * This function can be used to replace all stored licenses with provided data.
         *
         * @param {Array} data An array with new licenses.
         */
    replace(data) {

        _licenses = this.removeExpired(data);
        observableLicenses.onNext(_licenses);

    },

    /**
         * @ngdoc function
         * @name repositories.LicensesRepository#removeExpired
         * @methodOf repositories.LicensesRepository
         * @function
         *
         * @description
         * This function can be used to remove the expired licenses.
         *
         * @param {Array} data An array with all licenses.
         */
    removeExpired(data) {

        return data.filter(license => !license.attributes.expired);

    },

    /**
         * @ngdoc function
         * @name repositories.LicensesRepository#add
         * @methodOf repositories.LicensesRepository
         * @function
         *
         * @description
         * This function can be used to add a new license to repository.
         *
         * @param {object} license A license object.
         */
    add(license) {

        const indexOfLicense = _licenses
            .findIndex(_license => String(_license.id) === String(license.id));

        if (indexOfLicense === -1) {

            _licenses.push(license);
            observableLicenses.onNext(_licenses);

        }

    },

    /**
         * @ngdoc function
         * @name repositories.LicensesRepository#update
         * @methodOf repositories.LicensesRepository
         * @function
         *
         * @description
         * This function can be used to update a license from repository.
         *
         * @param {object} license a license object with new values.
         */
    update(license) {

        const indexOfLicense =
                _licenses.findIndex(_license => String(_license.id) === String(license.id));

        if (indexOfLicense >= 0) {

            _licenses[indexOfLicense] = license;
            observableLicenses.onNext(_licenses);

        }

    },

    /**
         * @ngdoc function
         * @name repositories.LicensesRepository#getAll
         * @methodOf repositories.LicensesRepository
         * @function
         *
         * @description
         * This function can be used to retrieves all licenses from repository.
         *
         * @returns {Array} licenses all licenses from repository
         */
    getAll() {

        return _licenses;

    },

    /**
         * @ngdoc function
         * @name rest.services.LicensesRepository#getLicensesByOs
         * @methodOf rest.services.LicensesRepository
         * @function
         *
         * @description
         * Returns the licenses for an os.
         *
         * @param {string} os
         *
         * @returns {Array}
         */
    getLicensesByOs(os) {

        const licensesByOs = [];

        _licenses.forEach((license) => {

            const appId = Util.deepFind(license, 'relationships.app.data.id');
            const app = AppIntegrationsRepository.getById(appId);
            const oses = Util.deepFind(app, 'attributes.custom.manifest.os');

            if (oses && _.contains(oses, os)) {

                return licensesByOs.push(license);

            }

            return false;

        });

        return licensesByOs;

    },

    /**
         * @ngdoc function
         * @name rest.services.LicensesRepository#getLicenseBy
         * @methodOf rest.services.LicensesRepository
         * @function
         *
         * @description
         * Returns the license object for an activation code
         *
         * @param {string} activationCode
         *
         * @returns {Object}
         */
    getLicenseBy(activationCode) {

        return _licenses.find(license => (Util.deepFind(license, 'relationships.activation-code.data.id') === activationCode));

    },

    /**
         * @ngdoc function
         * @name rest.services.LicensesRepository#getLicenseByAppId
         * @methodOf rest.services.LicensesRepository
         * @function
         *
         * @description
         * Returns the license object for a specific app id
         *
         * @param {string} appId
         *
         * @returns {Object}
         */
    getLicenseByAppId(appId) {

        return _licenses.find(license => (Util.deepFind(license, 'relationships.app.data.id') === appId));

    },

    /**
     * @ngdoc function
     * @name rest.services.LicensesRepository#getLicenseById
     * @methodOf rest.services.LicensesRepository
     * @function
     *
     * @description
     * Returns the license object for a specific license id
     *
     * @param {string} licenseId
     *
     * @returns {Object}
     */
    getLicenseById(licenseId) {

        return _licenses.find(license => license.id === licenseId);

    },

    /**
         * @ngdoc function
         * @name repositories.LicensesRepository#getObservable
         * @methodOf repositories.LicensesRepository
         * @function
         *
         * @description
         * This function can be used to get an observable object for licenses repository.
         * @returns {Object} an observable object for licenses repository.
         */
    getObservable() {

        return observableLicenses;

    },

    /**
         * @ngdoc function
         * @name repositories.LicensesRepository#deleteLicenseById
         * @methodOf repositories.LicensesRepository
         * @function
         *
         * @description
         * This function can be used to delete a specific license from repository based on id.
         * @returns {Object}
         */
    deleteLicenseById(licenseId) {

        _licenses = _licenses.filter(license => license.id !== licenseId);
        observableLicenses.onNext(_licenses);

    },

};

export default LicensesRepository;



// WEBPACK FOOTER //
// ./scripts/common/repositories/-RFC.licensesRepository.js